import { API_CONFIG } from '../config/api.config';

export type Availability = {
  mode: 'online' | 'paused' | 'starting' | 'unavailable' | 'offline';
  resumeAt?: string;
};

const json = async (url: string, signal: AbortSignal): Promise<unknown> => {
  const response = await fetch(url, { signal, cache: 'no-store', credentials: 'omit' });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

export const plannedPause = (value: unknown, now = Date.now()): Availability | null => {
  if (!value || typeof value !== 'object') return null;
  const status = value as Record<string, unknown>;
  if (status.version !== 1 || !['paused', 'starting'].includes(String(status.mode))) return null;
  const expires = typeof status.expiresAt === 'string' ? Date.parse(status.expiresAt) : NaN;
  const resume = typeof status.resumeAt === 'string' ? Date.parse(status.resumeAt) : NaN;
  if (!Number.isFinite(expires) || expires <= now || !Number.isFinite(resume)) return null;
  return { mode: status.mode === 'starting' || resume <= now ? 'starting' : 'paused', resumeAt: String(status.resumeAt) };
};

export const checkAvailability = async (signal: AbortSignal): Promise<Availability> => {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return { mode: 'offline' };
  const api = json(`${API_CONFIG.BASE_URL.replace(/\/$/, '')}/categories?availability=${Date.now()}`, signal);
  // Read the small static notice concurrently; a sleeping origin can time out.
  const notice = json(`/service-status.json?availability=${Date.now()}`, signal).catch(() => null);
  try {
    const payload = await api;
    if (payload && typeof payload === 'object' && 'success' in payload && payload.success === true) {
      // A healthy API wins even if a stale controller still announces maintenance.
      return { mode: 'online' };
    }
  } catch { /* Network errors and timeouts are availability signals, not auth failures. */ }
  return plannedPause(await notice) ?? { mode: 'unavailable' };
};
